import type { Tenant } from '../types/tenant.types';
import { apiFetch } from './client';
import { parseJsonResponse, type PaginationMeta } from './parseResponse';

export type DemoTenantListItem = Tenant & {
  is_demo?: boolean;
  demo_days?: number | null;
  days_remaining?: number | null;
  created_by?: {
    id?: number | null;
    name?: string | null;
    email?: string | null;
  } | null;
};

export type CreateDemoTenantPayload = {
  name: string;
  email: string;
  password: string;
  days: number;
  phone?: string;
};

export type CreateDemoTenantResult = {
  tenant: DemoTenantListItem;
  admin: { email: string; password: string };
  login_url: string;
  hostname_label?: string;
  demo_days?: number;
  subscription_ends_at?: string | null;
  warning?: string;
};

export type PromoteDemoTenantPayload = {
  plan_id: number;
  starts_at?: string;
  ends_at?: string;
  mark_as_paid?: boolean;
  amount?: number;
  payment_method?: string;
  payment_reference?: string;
  payment_notes?: string;
};

export type PromoteDemoTenantResult = {
  tenant: DemoTenantListItem;
  subscription: {
    id: number;
    status: string;
    plan_id: number;
    starts_at?: string | null;
    ends_at?: string | null;
  };
  payment?: {
    id: number;
    status: string;
    amount?: string;
    purpose?: string;
  } | null;
};

function buildList(
  data: DemoTenantListItem[] | undefined,
  meta?: PaginationMeta,
  perPage = 15,
) {
  const current = meta?.current_page ?? 1;
  const total = meta?.total ?? (Array.isArray(data) ? data.length : 0);
  const last = meta?.last_page ?? 1;
  return {
    current_page: current,
    data: Array.isArray(data) ? data : [],
    last_page: last,
    per_page: meta?.per_page ?? perPage,
    total,
  };
}

export async function fetchDemoTenantsList(
  page = 1,
  perPage = 15,
  filters?: { status?: string; search?: string; lifecycle?: 'active' | 'expired' },
): Promise<
  | { ok: true; list: ReturnType<typeof buildList> }
  | { ok: false; unauthorized?: boolean; message: string }
> {
  const lifecycle = filters?.lifecycle ?? 'active';
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  if (filters?.status) params.set('status', filters.status);
  if (filters?.search) params.set('search', filters.search);

  const path =
    lifecycle === 'expired'
      ? `/platform/demo-tenants/expired?${params.toString()}`
      : `/platform/demo-tenants?${params.toString()}`;

  const res = await apiFetch(path);
  const out = await parseJsonResponse<DemoTenantListItem[]>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, list: buildList(out.data, out.meta, perPage) };
}

export async function createDemoTenant(
  payload: CreateDemoTenantPayload,
): Promise<
  | { ok: true; data: CreateDemoTenantResult }
  | { ok: false; unauthorized?: boolean; message: string }
> {
  const res = await apiFetch('/platform/demo-tenants', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<CreateDemoTenantResult>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, data: out.data };
}

export async function promoteDemoTenant(
  tenantId: number,
  payload: PromoteDemoTenantPayload,
): Promise<
  | { ok: true; data: PromoteDemoTenantResult }
  | { ok: false; unauthorized?: boolean; message: string }
> {
  const res = await apiFetch(`/platform/demo-tenants/${encodeURIComponent(String(tenantId))}/promote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<PromoteDemoTenantResult>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, data: out.data };
}

export async function deleteDemoTenant(
  tenantId: number,
): Promise<{ ok: true } | { ok: false; unauthorized?: boolean; message: string }> {
  const res = await apiFetch(`/platform/demo-tenants/${encodeURIComponent(String(tenantId))}`, {
    method: 'DELETE',
  });
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}


export type TrialPerformanceActivityItem = {
  id: number;
  occurred_at: string | null;
  key: string;
  label: string;
  category: string;
  icon: string;
  step_key?: string | null;
};

export type TrialPerformanceReadModel = {
  trial: {
    id: number;
    name: string;
    slug: string;
    status: string;
    tenant_status: string;
    started_at: string | null;
    last_active_at: string | null;
    duration_days: number;
    created_at: string | null;
    subscription_ends_at: string | null;
    converted: boolean;
    expired: boolean;
  };
  overview: {
    health: number;
    activation: string;
    onboarding_percent: number;
    last_active_at: string | null;
  };
  health: {
    score: number;
    band: string;
    why: { key: string; label: string; points: number }[];
  };
  activation: {
    status: string;
    met: string[];
    missing: string[];
  };
  engagement: { level: string };
  onboarding: {
    completed: number;
    total: number;
    percent: number;
    status: string;
    steps: {
      key: string;
      title: string;
      order: number;
      status: string;
      completed_at: string | null;
      last_activity_at: string | null;
      last_activity_label: string;
    }[];
  };
  business_metrics: Record<string, number>;
  activity: {
    last: TrialPerformanceActivityItem | null;
    items: TrialPerformanceActivityItem[];
    meta: PaginationMeta;
  };
  purchase_signals: { key: string; label: string; done: boolean; value?: number }[];
  pricing: {
    pricing_viewed: boolean;
    upgrade_clicked: boolean;
    checkout_started: boolean;
    payment: boolean;
  };
  sales_priority: {
    level: string;
    reasons: { key: string; label: string }[];
  };
  lifecycle: { stages: string[]; current: string };
  recommendation: { key: string; text: string };
};

export async function fetchDemoTenantPerformance(
  tenantId: string | number,
  params?: { category?: string; period?: string; page?: number; per_page?: number },
): Promise<
  | { ok: true; data: TrialPerformanceReadModel }
  | { ok: false; unauthorized?: boolean; message: string }
> {
  const query = new URLSearchParams();
  if (params?.category) query.set('category', params.category);
  if (params?.period) query.set('period', params.period);
  if (params?.page) query.set('page', String(params.page));
  if (params?.per_page) query.set('per_page', String(params.per_page));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const res = await apiFetch(`/platform/demo-tenants/${tenantId}/performance${suffix}`);
  const out = await parseJsonResponse<TrialPerformanceReadModel>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, data: out.data };
}
